import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from 'app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';

import { FinanceService } from 'app/services/finance.service';
import { FinanceModel } from 'app/models/finance.model';
import { SaleService } from 'app/services/sale.service';
import { SaleModel } from 'app/models/sale.model';
import { ToastService } from 'app/shared/services/toast.service';
import { ApiService } from 'app/shared/apis/api.service';
import { UserModel } from 'app/models/user.model';
import { UserService } from 'app/services/user.service';
import { CompanyService } from 'app/services/company.service';
import { FinancialAccountService } from 'app/services/financial_account.service';
import { FinancialAccountModel } from 'app/models/financial_account.model';
import { EFinancialAccountOperation } from 'app/shared/enums/financial-account-operation.enum';
import { EFinancialAccountModule } from 'app/shared/enums/financial-account-module.enum';
import { FileFinanceService } from 'app/services/file_finance.service';
import { FileFinanceModel } from 'app/models/file_finance.model';
import { S3Service } from 'app/shared/services/s3.service';
import { ImageViewerService } from 'app/shared/services/image-viewer.service';

import { finalize, take, forkJoin } from 'rxjs';
import { paymentTypeOptions } from 'app/shared/options/payment-type.options';
import { Option } from 'app/shared/interfaces/option.interface';
import { EPaymentType } from 'app/shared/enums/payment-type.enum';

type FinanceForm = FormGroup<{
    sale_id: FormControl<number>;
    date: FormControl<Option<Date>>;
    competance_date: FormControl<Option<Date>>;
    finance_id: FormControl<Option<number>>;
    value: FormControl<Option<number>>;
    negotiated_value: FormControl<Option<number>>;
    document_number: FormControl<Option<string>>;
    forecast_payment_type: FormControl<Option<EPaymentType>>;
    realized_payment_type: FormControl<Option<EPaymentType>>;
    date_payed: FormControl<Option<Date>>;
    installment: FormControl<Option<number>>;
    description: FormControl<Option<string>>;
    user_receiver_id: FormControl<Option<number>>;
    user_payer_id: FormControl<Option<number>>;
    financial_account_id: FormControl<Option<number>>;
}>;

@Component({
    selector: 'app-finance-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        RouterModule,
        MatIconModule,
        MatSelectModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
        MatCheckboxModule,
        MatTooltipModule,
        MatDividerModule,
    ],
    providers: [provideNgxMask()],
    templateUrl: './finance-form.component.html',
})
export class FinanceFormComponent implements OnInit {
    form: FinanceForm = this.fb.group({
        sale_id: [null, Validators.required],
        finance_id: [null],
        date: [null, Validators.required],
        competance_date: [null],
        value: [null, Validators.required],
        negotiated_value: [null],
        document_number: [null],
        forecast_payment_type: [null, Validators.required],
        realized_payment_type: [null],
        date_payed: [null],
        installment: [1, [Validators.required, Validators.min(1)]],
        description: [null],
        user_receiver_id: [null],
        user_payer_id: [null],
        financial_account_id: [null],
    }) as FinanceForm;

    loading: boolean = false;
    loadingData: boolean = false;

    paymentTypeOptions = paymentTypeOptions;

    isEdit: boolean = false;
    disableDelete: boolean = false;
    saleData: SaleModel | null = null;
    existingFinances: FinanceModel[] = [];
    totalExistingValue: number = 0;
    loggedUser: UserModel | null = null;
    financialAccounts: FinancialAccountModel[] = [];
    groupedFinancialAccounts: any = {};
    selectedFinancialAccount: FinancialAccountModel | null = null;
    isOutrosModule: boolean = false;

    employeeOptions: UserModel[] = [];
    filteredEmployeeOptions: UserModel[] = [];
    employeeSearchControl = new FormControl('');
    private readonly payrollAccountTitles = ['salários', 'salarios', 'bônus', 'bonus', 'desconto folha'];

    // Anexos
    attachments: FileFinanceModel[] = [];
    attachmentType: string = 'Documento';
    pendingAttachments: { file: File; type_file: string }[] = [];
    attachmentsToDelete: FileFinanceModel[] = [];
    private loadedImages: Set<string> = new Set();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        private service: FinanceService,
        private saleService: SaleService,
        private financialAccountService: FinancialAccountService,
        private fileFinanceService: FileFinanceService,
        private userService: UserService,
        private companyService: CompanyService,
        private s3: S3Service,
        private imageViewerService: ImageViewerService,
        private toast: ToastService,
        private route: ActivatedRoute,
        private router: Router,
        private dialogRef: MatDialogRef<FinanceFormComponent>,
        private dialog: MatDialog,
        private api: ApiService,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
        this.disableDelete = !!data?.disableDelete;
    }

    ngOnInit(): void {
        // Detectar se está no módulo "Outros" (rota ou atalho RH / forceOutrosModule)
        const currentUrl = this.router.url;
        this.isOutrosModule =
            currentUrl.includes('/finance/outros') ||
            !!this.data?.forceOutrosModule ||
            (!this.data?.sale_id && !!this.data?.user_receiver_id && !this.data?.finance_id);
        
        // Ajustar validação de sale_id baseado no módulo
        if (this.isOutrosModule) {
            this.form.get('sale_id')?.clearValidators();
            this.form.get('sale_id')?.updateValueAndValidity();
            
            // Tornar financial_account_id obrigatório no módulo Outros
            this.form.get('financial_account_id')?.setValidators([Validators.required]);
            this.form.get('financial_account_id')?.updateValueAndValidity();
            this.form.get('document_number')?.setValidators([Validators.required]);
            this.form.get('document_number')?.updateValueAndValidity();
            this.loadEmployees();
        }

        this.form.get('financial_account_id')?.valueChanges.subscribe((accountId) => {
            this.loadSelectedFinancialAccount(accountId);
            this.updateEmployeeReceiverValidators();
            this.applyDefaultEmployeeReceiver();
        });

        this.employeeSearchControl.valueChanges.subscribe((term) => this.filterEmployees(term || ''));
        
        this.getFinance();
        this.isEdit = !!this.data?.finance_id;
        this.loadUser();
        this.loadSaleAndFinances();
        this.loadFinancialAccounts();
        if (this.isEdit && this.data?.finance_id) {
            setTimeout(() => {
                this.loadAttachments();
            }, 100);
        } else {
            // Para novos registros, inicializar a lista de anexos vazia
            this.attachments = [];
        }
    }

    private normalizeAccountLabel(value: string | null | undefined): string {
        return String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    isEmployeePayrollAccount(): boolean {
        const title = this.normalizeAccountLabel(this.selectedFinancialAccount?.title);
        const group = this.normalizeAccountLabel(this.selectedFinancialAccount?.group);
        return group === 'salarios' || this.payrollAccountTitles.includes(title);
    }

    showEmployeeReceiverField(): boolean {
        if (!this.isOutrosModule) return false;
        return (
            this.isEmployeePayrollAccount() ||
            !!this.data?.forceOutrosModule ||
            !!this.form.get('user_receiver_id')?.value
        );
    }

    updateEmployeeReceiverValidators(): void {
        const control = this.form.get('user_receiver_id');
        if (!control) return;
        if (this.showEmployeeReceiverField() && this.isEmployeePayrollAccount()) {
            control.setValidators([Validators.required]);
        } else if (this.data?.forceOutrosModule) {
            control.setValidators([Validators.required]);
        } else {
            control.clearValidators();
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * No create do Outros, pré-seleciona o usuário logado como Funcionário (ainda editável).
     * Não sobrescreve valor já vindo de atalho RH / edição.
     */
    applyDefaultEmployeeReceiver(): void {
        if (this.isEdit || !this.isOutrosModule || !this.loggedUser?.user_id) return;
        if (this.data?.user_receiver_id != null) return;
        const control = this.form.get('user_receiver_id');
        if (!control || control.value != null) return;
        control.setValue(this.loggedUser.user_id, { emitEvent: false });
        this.updateEmployeeReceiverValidators();
    }

    loadEmployees(): void {
        const selectedCompanyId = this.companyService.getSelectedCompanyId();
        const params: any = { limit: 'all' };
        if (selectedCompanyId) params.company_id = selectedCompanyId;

        this.userService
            .listInternalUsers(params)
            .pipe(take(1))
            .subscribe({
                next: (res) => {
                    this.employeeOptions = (res.items ?? []).map((u) => new UserModel(u));
                    this.filteredEmployeeOptions = [...this.employeeOptions];
                    this.applyDefaultEmployeeReceiver();
                },
                error: () => {
                    this.employeeOptions = [];
                    this.filteredEmployeeOptions = [];
                },
            });
    }

    filterEmployees(searchText: string): void {
        const search = searchText.toLowerCase().trim();
        if (!search) {
            this.filteredEmployeeOptions = [...this.employeeOptions];
            return;
        }
        this.filteredEmployeeOptions = this.employeeOptions.filter(
            (u) =>
                u.name?.toLowerCase().includes(search) ||
                u.email?.toLowerCase().includes(search) ||
                u.doc_number?.toLowerCase().includes(search),
        );
    }

    loadUser(): void {
        ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
            this.loggedUser = new UserModel(user);
            this.applyDefaultEmployeeReceiver();
        });
    }

    canEditFinance(): boolean {
        if (!this.isEdit) {
            // Se não está editando, verifica se pode criar
            return this.loggedUser?.permission?.can_create_finance ?? false;
        }
        // Se está editando, verifica se pode editar
        return this.loggedUser?.permission?.can_edit_finance ?? false;
    }

    done(): void {
        this.loading = true;
        this.form.markAllAsTouched();
        if (!this.form.valid) {
            this.loading = false;
            return;
        }

        const formData = this.form.value;
        const financialAccountId = formData.financial_account_id;
        
        // Carregar financial_account se necessário
        if (financialAccountId && !this.selectedFinancialAccount) {
            this.loadSelectedFinancialAccount(financialAccountId);
        }

        // Aplicar validação baseado na operation da financial_account
        // ENTRADA = recebimentos, SAIDA = comissões
        // Apenas validar se houver sale_id (módulo VENDAS)
        // E apenas validar se estiver criando ou alterando o valor (não apenas outros campos como data)
        if (formData.sale_id) {
            const isValueChanging = this.data?.finance_id && formData.value !== this.data.value;
            const isCreating = !this.data?.finance_id;
            
            // Apenas validar valor se estiver criando ou alterando o valor
            if (isCreating || isValueChanging) {
                if (this.selectedFinancialAccount?.operation === EFinancialAccountOperation.ENTRADA || !financialAccountId) {
                    if (!this.validateTotalValue(formData.value || 0)) {
                        this.loading = false;
                        return;
                    }
                }
                // Para SAIDA (comissões), validar contra commission_value
                else if (this.selectedFinancialAccount?.operation === EFinancialAccountOperation.SAIDA) {
                    if (!this.validateCommissionValue(formData.value || 0)) {
                        this.loading = false;
                        return;
                    }
                }
            }
        }

        const payload: any = {
            ...formData,
            ...(this.data?.finance_id
                ? { financial_account_id: formData.financial_account_id ?? this.data?.financial_account_id ?? null }
                : {}),
        };
        if (this.isOutrosModule) {
            const negotiated = formData.negotiated_value;
            payload.negotiated_value =
                negotiated === null || negotiated === undefined || negotiated === ('' as any) ? null : Number(negotiated);

            const docNumber = String(formData.document_number || '').trim();
            if (!docNumber) {
                this.toast.error('Número do Documento é obrigatório.');
                this.loading = false;
                return;
            }
            payload.document_number = docNumber;

            // Atalho RH / Outros: nunca vincular a venda; garantir receiver do data se o control estiver vazio
            payload.sale_id = null;
            if (
                (payload.user_receiver_id == null || payload.user_receiver_id === '') &&
                this.data?.user_receiver_id != null
            ) {
                payload.user_receiver_id = this.data.user_receiver_id;
            }

            const accountId = payload.financial_account_id ?? financialAccountId;
            const account =
                this.selectedFinancialAccount ||
                this.financialAccounts.find((a) => a.financial_account_id === accountId);
            if (!accountId || !account || account.module !== EFinancialAccountModule.OUTROS) {
                this.toast.error('Selecione uma conta do módulo Outros.');
                this.loading = false;
                return;
            }
        } else {
            delete payload.negotiated_value;
            delete payload.document_number;
        }
        const data = new FinanceModel(payload);

        if (this.data?.finance_id) {
            this.service
                .update(data)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe(async (updatedFinance) => {
                    if (this.attachmentsToDelete.length) {
                        await this.processPendingDeletions();
                    }
                    if (this.pendingAttachments.length) {
                        await this.processPendingUploads(this.data.finance_id);
                    }
                    this.dialogRef.close(updatedFinance || true);
                });
        } else {
            this.service
                .create(data)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe(async (createdFinance) => {
                    const financeId = (createdFinance as any)?.finance_id;
                    if (financeId && this.pendingAttachments.length) {
                        await this.processPendingUploads(financeId);
                    }
                    // Retornar o finance criado para que o componente pai possa criar a comissão
                    this.dialogRef.close(createdFinance);
                });
        }
    }

    clear(): void {
        this.dialogRef.close(false);
    }

    deleteFinance(): void {
        if (!this.data?.finance_id || this.loading) {
            return;
        }

        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: 'Remover lançamento',
                    message: 'Deseja remover este lançamento financeiro?',
                    confirmText: 'Remover',
                    cancelText: 'Cancelar',
                } as ConfirmDialogData,
                width: '480px',
                panelClass: 'card-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((ok) => {
                if (!ok) return;

                this.loading = true;

                this.service
                    .deleteFinance(this.data.finance_id)
                    .pipe(
                        take(1),
                        finalize(() => (this.loading = false)),
                    )
                    .subscribe((result) => {
                        this.toast.success('Lançamento financeiro excluído com sucesso!');

                        const deletedCount = result?.deleted_commission_ids?.length ?? 0;
                        if (deletedCount > 0) {
                            const plural = deletedCount > 1 ? 'comissões' : 'comissão';
                            this.toast.info(`${deletedCount} ${plural} associada(s) foram excluídas automaticamente.`);
                        }

                        this.dialogRef.close(result || true);
                    });
            });
    }

    getFinance(): void {
        const id = this.data?.finance_id;
        if (!id) {
            if (this.data) {
                this.form.patchValue({
                    sale_id: this.data.sale_id ?? null,
                    installment: 1,
                    date: new Date(),
                    competance_date: new Date(),
                    user_receiver_id: this.data.user_receiver_id ?? null,
                    user_payer_id: this.data.user_payer_id ?? null,
                    financial_account_id: this.data.financial_account_id ?? null,
                });
            }
            return;
        }
        this.loadingData = true;
        this.service
            .getById(+id)
            .pipe(
                take(1),
                finalize(() => (this.loadingData = false)),
            )
            .subscribe((data) => {
                this.data = data;
                this.form.patchValue({
                    finance_id: data.finance_id,
                    sale_id: data.sale_id,
                    date: data.date,
                    competance_date: data.competance_date,
                    value: data.value,
                    negotiated_value: data.negotiated_value ?? null,
                    document_number: data.document_number ?? null,
                    forecast_payment_type: data.forecast_payment_type,
                    realized_payment_type: data.realized_payment_type,
                    date_payed: data.date_payed || null,
                    installment: data.installment || 1,
                    description: data.description,
                    user_receiver_id: data.user_receiver_id || null,
                    user_payer_id: data.user_payer_id || null,
                    financial_account_id: data.financial_account_id || null,
                });
                
                // Carregar financial_account selecionada
                if (data.financial_account_id) {
                    this.loadSelectedFinancialAccount(data.financial_account_id);
                }
                
                // Carregar attachments
                this.loadAttachments();
            });
    }

    loadSaleAndFinances(): void {
        if (!this.data?.sale_id) {
            return;
        }

        this.loadingData = true;
        const moduleFilter = this.isOutrosModule
            ? EFinancialAccountModule.OUTROS
            : EFinancialAccountModule.VENDAS;

        // Carregar venda, finances e contas no mesmo forkJoin para accountMap sempre preenchido
        forkJoin({
            sale: this.saleService.getById(this.data.sale_id),
            finances: this.service.list({ sale_id: this.data.sale_id, limit: 'all' }),
            financialAccounts: this.financialAccountService.list({ limit: 'all', module: moduleFilter }),
        })
            .pipe(
                take(1),
                finalize(() => (this.loadingData = false)),
            )
            .subscribe({
                next: (result) => {
                    this.saleData = result.sale;
                    const accounts = result.financialAccounts?.items ?? [];
                    if (accounts.length && !this.financialAccounts.length) {
                        this.financialAccounts = accounts.filter(
                            (acc: FinancialAccountModel) => acc.module === moduleFilter,
                        );
                        this.groupFinancialAccounts();
                    }

                    // Mapa de contas a partir do resultado do forkJoin (nunca vazio quando há contas)
                    const accountMap = new Map(
                        accounts.map((acc: FinancialAccountModel) => [acc.financial_account_id, acc]),
                    );

                    const currentFinancialAccountId =
                        this.form.get('financial_account_id')?.value || this.data?.financial_account_id;
                    const currentAccount = currentFinancialAccountId ? accountMap.get(currentFinancialAccountId) : null;

                    if (currentAccount?.operation === EFinancialAccountOperation.SAIDA) {
                        this.existingFinances = result.finances.items;
                    } else {
                        this.existingFinances = result.finances.items.filter((f: FinanceModel) => {
                            const acc = f.financial_account_id ? accountMap.get(f.financial_account_id) : null;
                            return !acc || acc.operation === EFinancialAccountOperation.ENTRADA;
                        });
                    }

                    if (this.isEdit && this.data?.finance_id) {
                        this.existingFinances = this.existingFinances.filter(
                            (f: FinanceModel) => f.finance_id !== this.data?.finance_id,
                        );
                    }

                    // Total já lançado: ENTRADA, ou sem conta no lançamento, ou conta não está no mapa (evitar "Já lançado: 0")
                    const receivableFinances = this.existingFinances.filter((f: FinanceModel) => {
                        const acc =
                            f.financial_account_id != null ? accountMap.get(f.financial_account_id) : null;
                        return (
                            (acc != null && acc.operation === EFinancialAccountOperation.ENTRADA) ||
                            f.financial_account_id == null ||
                            acc == null
                        );
                    });
                    this.totalExistingValue = receivableFinances.reduce(
                        (sum, f) => sum + (Number(f.value) || 0),
                        0,
                    );
                },
                error: (error) => {
                    console.error('Erro ao carregar dados da venda:', error);
                    this.toast.error('Erro ao carregar dados da venda');
                },
            });
    }

    validateTotalValue(newValue: number): boolean {
        if (!this.saleData?.final_value) {
            return true; // Se não tem venda, não valida
        }

        const totalWithNewValue = this.totalExistingValue + newValue;
        
        if (totalWithNewValue > this.saleData.final_value) {
            const remaining = this.saleData.final_value - this.totalExistingValue;
            this.toast.error(
                `O valor ultrapassa o total da venda! Valor da venda: R$ ${this.saleData.final_value.toFixed(2)} | Já lançado: R$ ${this.totalExistingValue.toFixed(2)} | Disponível: R$ ${remaining.toFixed(2)}`
            );
            return false;
        }

        return true;
    }

    getRemainingValue(): number {
        const financialAccountId = this.form.get('financial_account_id')?.value || this.data?.financial_account_id;
        
        // Se for comissão (SAIDA), retornar valor disponível de comissão
        if (this.selectedFinancialAccount?.operation === EFinancialAccountOperation.SAIDA) {
            return this.getRemainingCommissionValue();
        }
        
        // Caso contrário, retornar valor disponível de recebimento
        if (!this.saleData?.final_value) {
            return 0;
        }
        return Math.max(0, this.saleData.final_value - this.totalExistingValue);
    }

    /**
     * Retorna o total de comissões já lançadas (SAIDA)
     */
    getTotalExistingCommissions(): number {
        // Filtrar finances que têm financial_account com operation = SAIDA
        const existingCommissions = this.existingFinances.filter((f: FinanceModel) => {
            const account = this.financialAccounts.find(acc => acc.financial_account_id === f.financial_account_id);
            return account?.operation === EFinancialAccountOperation.SAIDA;
        });
        return existingCommissions.reduce(
            (sum, f) => sum + (Number(f.value) || 0),
            0
        );
    }

    /**
     * Retorna o valor disponível de comissão
     */
    getRemainingCommissionValue(): number {
        if (!this.saleData?.commission_value) {
            return 0;
        }
        return Math.max(0, this.saleData.commission_value - this.getTotalExistingCommissions());
    }

    /**
     * Verifica se está editando/criando uma comissão (SAIDA)
     */
    isCommission(): boolean {
        return this.selectedFinancialAccount?.operation === EFinancialAccountOperation.SAIDA;
    }

    /**
     * Valida se o valor da comissão não excede o commission_value da venda
     */
    validateCommissionValue(newValue: number): boolean {
        if (!this.saleData?.commission_value) {
            return true; // Se não tem comissão definida, não valida
        }

        // Calcular total de comissões já lançadas (SAIDA)
        const totalCommissions = this.getTotalExistingCommissions();

        const totalWithNewValue = totalCommissions + newValue;

        if (totalWithNewValue > this.saleData.commission_value) {
            const remaining = this.saleData.commission_value - totalCommissions;
            this.toast.error(
                `O valor da comissão ultrapassa o total permitido! Valor da comissão da venda: R$ ${this.saleData.commission_value.toFixed(2)} | Já lançado: R$ ${totalCommissions.toFixed(2)} | Disponível: R$ ${remaining.toFixed(2)}`
            );
            return false;
        }

        return true;
    }

    loadFinancialAccounts(): void {
        // Usar a propriedade já detectada no ngOnInit
        const moduleFilter = this.isOutrosModule 
            ? EFinancialAccountModule.OUTROS 
            : EFinancialAccountModule.VENDAS;
        
        this.financialAccountService
            .list({ limit: 'all', module: moduleFilter })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    // Filtrar também no frontend para garantir
                    this.financialAccounts = response.items.filter(
                        account => account.module === moduleFilter
                    );
                    this.groupFinancialAccounts();
                    const selectedId = this.form.get('financial_account_id')?.value || this.data?.financial_account_id;
                    if (selectedId) {
                        this.loadSelectedFinancialAccount(selectedId);
                    } else {
                        this.updateEmployeeReceiverValidators();
                    }
                },
                error: (error) => {
                    console.error('Erro ao carregar contas financeiras:', error);
                },
            });
    }

    loadSelectedFinancialAccount(accountId: number | null): void {
        if (!accountId) {
            this.selectedFinancialAccount = null;
            this.updateEmployeeReceiverValidators();
            return;
        }

        const account = this.financialAccounts.find(acc => acc.financial_account_id === accountId);
        if (account) {
            this.selectedFinancialAccount = account;
            this.updateEmployeeReceiverValidators();
        } else {
            // Se não encontrou no array, carregar do serviço
            this.financialAccountService
                .getById(accountId)
                .pipe(take(1))
                .subscribe({
                    next: (accountData) => {
                        this.selectedFinancialAccount = accountData;
                        this.updateEmployeeReceiverValidators();
                    },
                    error: (error) => {
                        console.error('Erro ao carregar conta financeira:', error);
                        this.selectedFinancialAccount = null;
                        this.updateEmployeeReceiverValidators();
                    },
                });
        }
    }

    groupFinancialAccounts(): void {
        const grouped: any = {};

        this.financialAccounts.forEach((account) => {
            const operation = account.operation || 'OUTROS';
            const group = account.group || 'Sem Grupo';
            const title = account.title || 'Sem Título';

            if (!grouped[operation]) {
                grouped[operation] = {};
            }
            if (!grouped[operation][group]) {
                grouped[operation][group] = [];
            }
            grouped[operation][group].push(account);
        });

        this.groupedFinancialAccounts = grouped;
    }

    getOperationLabel(value: string): string {
        switch (value) {
            case EFinancialAccountOperation.ENTRADA:
                return 'Entrada';
            case EFinancialAccountOperation.SAIDA:
                return 'Saída';
            default:
                return value;
        }
    }

    getModuleLabel(value: string): string {
        switch (value) {
            case EFinancialAccountModule.VENDAS:
                return 'Vendas';
            case EFinancialAccountModule.OUTROS:
                return 'Outros';
            default:
                return value;
        }
    }

    getOperationKeys(): string[] {
        return Object.keys(this.groupedFinancialAccounts).sort();
    }

    getGroupKeys(operation: string): string[] {
        if (!this.groupedFinancialAccounts[operation]) {
            return [];
        }
        return Object.keys(this.groupedFinancialAccounts[operation]).sort();
    }

    getAccountsByOperationAndGroup(operation: string, group: string): FinancialAccountModel[] {
        if (!this.groupedFinancialAccounts[operation] || !this.groupedFinancialAccounts[operation][group]) {
            return [];
        }
        return this.groupedFinancialAccounts[operation][group];
    }

    canUploadFilesOnFinance(): boolean {
        return this.loggedUser?.permission?.can_upload_files_on_sale ?? false;
    }

    loadAttachments(): void {
        if (!this.data?.finance_id) return;
        
        this.loadedImages.clear();
        
        this.fileFinanceService
            .list({ finance_id: this.data.finance_id } as any)
            .pipe(take(1))
            .subscribe((resp) => {
                this.attachments = (resp.items || []).map((item: any) => {
                    const fileFinance = new FileFinanceModel(item);
                    if (item.user) {
                        fileFinance.user = new UserModel(item.user);
                    }
                    return fileFinance;
                });
            });
    }

    async onSelectFiles(event: Event): Promise<void> {
        if (!this.canUploadFilesOnFinance()) {
            this.toast.warning('Você não tem permissão para fazer upload de arquivos.');
            return;
        }
        const input = event.target as HTMLInputElement;
        if (!input.files) return;
        const files = Array.from(input.files);
        for (const file of files) {
            this.pendingAttachments.push({ file, type_file: this.attachmentType });
        }
        this.toast.info('Arquivo(s) adicionados. Clique em Salvar para enviar.');
    }

    async remove(file: FileFinanceModel): Promise<void> {
        this.attachmentsToDelete.push(file);
        this.attachments = this.attachments.filter((a) => a.file_finance_id !== file.file_finance_id);
        this.toast.info('Arquivo marcado para exclusão. Clique em Salvar para confirmar.');
    }

    async processPendingDeletions(): Promise<void> {
        for (const file of this.attachmentsToDelete) {
            try {
                await this.s3.deleteFile(file.path);
                await this.fileFinanceService.delete(file).pipe(take(1)).toPromise();
            } catch (error) {
                console.error('Erro ao deletar arquivo:', file.path, error);
                this.toast.error(`Erro ao deletar arquivo: ${this.getFileName(file.path)}`);
            }
        }
        this.attachmentsToDelete = [];
    }

    async download(file: FileFinanceModel): Promise<void> {
        if (this.isImage(file) || this.isPdf(file)) {
            await this.imageViewerService.openInNewTab(file.path);
            return;
        }

        try {
            const normalizedKey = this.normalizeS3Key(file.path);
            const response = await this.api
                .postSub<{ url: string }, { urlOrKey: string }>('storage/download-url', { urlOrKey: normalizedKey })
                .pipe(take(1))
                .toPromise();
            const presignedUrl = response.url;

            if (!presignedUrl) {
                throw new Error('Failed to get presigned URL.');
            }

            const fetchResponse = await fetch(presignedUrl, {
                method: 'GET',
                credentials: 'omit',
            });

            if (!fetchResponse.ok) {
                throw new Error(`HTTP error! status: ${fetchResponse.status}`);
            }

            const blob = await fetchResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = this.getFileName(file.path);
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 200);
        } catch (e) {
            console.error('Erro ao baixar arquivo:', e);
            this.toast.error('Erro ao baixar arquivo. Tentando abrir em nova guia.');
            try {
                const normalizedKey = this.normalizeS3Key(file.path);
                const response = await this.api
                    .postSub<{ url: string }, { urlOrKey: string }>('storage/download-url', { urlOrKey: normalizedKey })
                    .pipe(take(1))
                    .toPromise();
                if (response.url) {
                    window.open(response.url, '_blank');
                }
            } catch (fallbackError) {
                console.error('Fallback também falhou:', fallbackError);
                this.toast.error('Não foi possível baixar o arquivo.');
            }
        }
    }

    private normalizeS3Key(url: string): string {
        if (!url) {
            return '';
        }

        const cdnPrefix = 'https://cdn.artsys.com/';
        if (url.startsWith(cdnPrefix)) {
            return url.slice(cdnPrefix.length);
        }

        const knownPrefixes = ['https://evobrides.s3.amazonaws.com/', 'https://art-sys.s3.amazonaws.com/'];

        for (const prefix of knownPrefixes) {
            if (url.startsWith(prefix)) {
                return url.slice(prefix.length);
            }
        }

        try {
            const parsed = new URL(url);
            if (parsed.hostname.endsWith('s3.amazonaws.com')) {
                return parsed.pathname.replace(/^\/+/, '');
            }
        } catch (error) {
            // Ignore URL parsing errors and fall back to original url below
        }

        return url;
    }

    removePending(index: number): void {
        this.pendingAttachments.splice(index, 1);
    }

    async saveAttachments(): Promise<void> {
        if (!this.data?.finance_id) {
            this.toast.error('Não é possível salvar anexos sem um lançamento financeiro associado');
            return;
        }

        if (this.attachmentsToDelete.length === 0 && this.pendingAttachments.length === 0) {
            this.toast.info('Nenhuma alteração para salvar');
            return;
        }

        this.loading = true;
        try {
            if (this.attachmentsToDelete.length) {
                await this.processPendingDeletions();
            }
            if (this.pendingAttachments.length) {
                await this.processPendingUploads(this.data.finance_id);
            }
            this.toast.success('Anexos atualizados com sucesso');
        } catch (error) {
            console.error('Erro ao salvar anexos:', error);
            this.toast.error('Erro ao salvar anexos. Tente novamente.');
        } finally {
            this.loading = false;
        }
    }

    async processPendingUploads(financeId: number): Promise<void> {
        for (const item of this.pendingAttachments) {
            const formData = new FormData();
            formData.append('file', item.file);
            formData.append('path', 'finances/attachments');
            formData.append('name', item.file.name);
            const resp = await this.api.postSub<{ url: string }, FormData>('storage/upload', formData).pipe(take(1)).toPromise();
            const url = resp.url;
            await this.fileFinanceService
                .create({ path: url, type_file: item.type_file, finance_id: financeId, user_id: this.loggedUser?.user_id, name: item.file.name } as any)
                .pipe(take(1))
                .toPromise();
        }
        this.pendingAttachments = [];
        this.loadAttachments();
    }

    getFileName(path: string): string {
        if (!path) return '';
        try {
            const decoded = decodeURIComponent(path);
            const parts = decoded.split('/');
            return parts[parts.length - 1] || decoded;
        } catch {
            const parts = path.split('/');
            return parts[parts.length - 1] || path;
        }
    }

    getFileDisplayName(file: FileFinanceModel): string {
        return file.name || this.getFileName(file.path);
    }

    isImage(file: FileFinanceModel): boolean {
        const fileName = file.name || this.getFileName(file.path);
        if (!fileName) return false;

        const extension = fileName.toLowerCase().split('.').pop();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

        return imageExtensions.includes(extension);
    }

    isPdf(file: FileFinanceModel): boolean {
        const fileName = file.name || this.getFileName(file.path);
        if (!fileName) return false;

        const extension = fileName.toLowerCase().split('.').pop();
        return extension === 'pdf';
    }

    getFileIcon(file: FileFinanceModel): string {
        if (this.isImage(file)) {
            return 'heroicons_outline:photo';
        }
        if (this.isPdf(file)) {
            return 'heroicons_outline:document-text';
        }
        return 'heroicons_outline:paper-clip';
    }

    onFileClick(file: FileFinanceModel, event?: Event): void {
        if (event) {
            const target = event.target as HTMLElement;
            if (target.closest('button') || target.closest('.flex.items-center.gap-1')) {
                return;
            }
        }

        if (this.isImage(file) || this.isPdf(file)) {
            this.openViewer(file);
        }
    }

    openViewer(file: FileFinanceModel): void {
        if (!file || !file.path) {
            this.toast.warning('Arquivo não disponível');
            return;
        }

        if (!this.isImage(file) && !this.isPdf(file)) {
            this.download(file);
            return;
        }

        try {
            if (this.isImage(file)) {
                const allImageUrls = this.attachments
                    .filter((a) => a && this.isImage(a) && a.path)
                    .map((a) => a.path)
                    .filter((path) => path && path.trim() !== '');

                if (allImageUrls.length === 0) {
                    this.toast.warning('Nenhuma imagem disponível');
                    return;
                }

                const currentIndex = allImageUrls.indexOf(file.path);
                this.imageViewerService.openLightbox(file.path, allImageUrls, currentIndex >= 0 ? currentIndex : 0);
            } else if (this.isPdf(file)) {
                this.imageViewerService.openInNewTab(file.path);
            }
        } catch (error) {
            console.error('Erro ao abrir visualizador:', error);
            this.toast.error('Erro ao abrir arquivo. Tente novamente.');
        }
    }
}
